import React from 'react'
import { motion } from 'framer-motion'
import { ShieldCheck, Globe, Zap } from 'lucide-react'
export function AboutSection() {
  const stats = [
    {
      label: 'Years Research',
      value: '15+',
    },
    {
      label: 'Global Partners',
      value: '12',
    },
    {
      label: 'Target Capacity',
      value: '50MW',
    },
  ]
  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <motion.div
            initial={{
              opacity: 0,
              x: -30,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.8,
            }}
          >
            <h2 className="text-ocean-500 font-semibold tracking-wider uppercase text-sm mb-3">
              About ENERSIC
            </h2>
            <h3 className="text-3xl md:text-4xl font-bold text-navy-900 mb-6">
              Bridging the gap between ocean potential and reliable power.
            </h3>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              ENERSIC is a renewable energy company dedicated to unlocking the
              vast potential of wave energy. We don't just build technology; we
              design and deploy complete power solutions that integrate
              seamlessly into existing grids.
            </p>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              By partnering with proven Wave Energy Converter (WEC) technology
              providers, we deliver sustainable, low-carbon energy specifically
              tailored for coastal regions and island communities facing energy
              security challenges.
            </p>

            <div className="space-y-4 mb-10">
              <div className="flex items-start">
                <div className="flex-shrink-0 p-2 bg-ocean-50 rounded-lg text-ocean-900">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-semibold text-navy-900">
                    Energy Security
                  </h4>
                  <p className="text-gray-600">
                    Reducing dependence on imported fossil fuels for remote
                    locations.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 p-2 bg-ocean-50 rounded-lg text-ocean-900">
                  <Globe className="h-6 w-6" />
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-semibold text-navy-900">
                    Decarbonization
                  </h4>
                  <p className="text-gray-600">
                    Zero-emission power generation operating 24/7.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-8 border-t border-gray-100 pt-8">
              {stats.map((stat, index) => (
                <div key={index}>
                  <div className="text-3xl font-bold text-ocean-900 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-500 font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Image/Visual Placeholder */}
          <motion.div
            initial={{
              opacity: 0,
              x: 30,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.8,
              delay: 0.2,
            }}
            className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl bg-ocean-900"
          >
            {/* Abstract representation of ocean energy */}
            <div className="absolute inset-0 bg-gradient-to-t from-ocean-900 to-transparent z-10" />

            {/* SVG Illustration of Coastline/Waves */}
            <svg
              className="absolute inset-0 w-full h-full object-cover"
              viewBox="0 0 800 600"
              preserveAspectRatio="xMidYMid slice"
            >
              <defs>
                <linearGradient
                  id="waterGrad"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#088395" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#0A4D68" stopOpacity="1" />
                </linearGradient>
              </defs>
              <rect width="100%" height="100%" fill="url(#waterGrad)" />
              <circle
                cx="400"
                cy="300"
                r="150"
                fill="#00C9A7"
                fillOpacity="0.1"
              />
              <circle
                cx="400"
                cy="300"
                r="250"
                fill="#00C9A7"
                fillOpacity="0.05"
              />
              <path
                d="M0,400 Q200,350 400,400 T800,400 V600 H0 Z"
                fill="#0A4D68"
              />
            </svg>

            <div className="absolute bottom-8 left-8 right-8 z-20">
              <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/20">
                <div className="flex items-center space-x-3 mb-2">
                  <Zap className="h-5 w-5 text-ocean-500" />
                  <span className="text-white font-semibold">
                    Consistent Power
                  </span>
                </div>
                <p className="text-ocean-50 text-sm">
                  Waves offer higher density and predictability than wind or
                  solar, providing a stable baseload power source.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
